import * as Sentry from "@sentry/react";
import { type JSX, type ReactNode } from "react";
import { defineMessages, useIntl } from "react-intl";

import FullPageLoader from "./statePages/FullPageLoader";
import GlobalErrorPage from "./statePages/GlobalErrorPage";

import { env } from "@/app/utils/env";
import {
  claimChunkLoadReload,
  hasChunkLoadReloadAttempt,
  isChunkLoadFailure,
} from "@/sentry/chunkLoadError";

interface ErrorBoundaryProps {
  children: ReactNode;
}

const errorBoundaryMessages = defineMessages({
  errorDetailsHeading: {
    id: "components.errorBoundary.errorDetailsHeading",
    defaultMessage: "Error Details",
  },
  errorLabel: {
    id: "components.errorBoundary.errorLabel",
    defaultMessage: "Error:",
  },
  genericErrorFallback: {
    id: "components.errorBoundary.genericErrorFallback",
    defaultMessage: "Unknown error",
  },
  noStackTraceFallback: {
    id: "components.errorBoundary.noStackTraceFallback",
    defaultMessage: "No stack trace available.",
  },
  reloadAction: {
    id: "components.errorBoundary.reloadAction",
    defaultMessage: "Reload",
  },
  stackTraceLabel: {
    id: "components.errorBoundary.stackTraceLabel",
    defaultMessage: "Stack Trace:",
  },
  title: {
    id: "components.errorBoundary.title",
    defaultMessage: "Oops! Something went wrong",
  },
});

interface LocalErrorFallbackProps {
  error: unknown;
  componentStack: string;
}

let pendingChunkLoadReload = false;

/**
 * Renders a developer-focused fallback panel when the boundary trips in local builds.
 * Includes a reload control and a formatted React component stack for quick debugging.
 *
 * @param {LocalErrorFallbackProps} props - Value thrown by the React tree (can be any type).
 * @returns {JSX.Element} React element showing error details and stack trace.
 */
const LocalErrorFallback = (props: LocalErrorFallbackProps): JSX.Element => {
  const intl = useIntl();
  const { error, componentStack } = props;

  const stackLines = componentStack
    ? componentStack
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    : [intl.formatMessage(errorBoundaryMessages.noStackTraceFallback)];

  return (
    <div className='error-boundary max-w-4xl mx-auto my-10 p-8 border border-destructive rounded-xl bg-destructive/5 shadow'>
      <h2 className='text-2xl font-semibold text-destructive mb-4'>
        {intl.formatMessage(errorBoundaryMessages.title)}
      </h2>
      <button
        className='cursor-pointer text-white bg-destructive border-none rounded-md px-6 py-2 mb-5 text-sm font-semibold'
        onClick={() => window.location.reload()}
      >
        {intl.formatMessage(errorBoundaryMessages.reloadAction)}
      </button>
      <details open>
        <summary className='text-base font-semibold mb-2 cursor-pointer'>
          {intl.formatMessage(errorBoundaryMessages.errorDetailsHeading)}
        </summary>
        <div className='mb-2'>
          <strong>
            {intl.formatMessage(errorBoundaryMessages.errorLabel)}
          </strong>{" "}
          {String(
            error ||
              intl.formatMessage(errorBoundaryMessages.genericErrorFallback),
          )}
        </div>
        <div>
          <strong>
            {intl.formatMessage(errorBoundaryMessages.stackTraceLabel)}
          </strong>
          <ul className='font-mono text-xs mt-2 bg-background p-2 rounded border'>
            {stackLines.map((line, idx) => (
              <li key={idx} className='mb-1'>
                {line}
              </li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
};

/**
 * Sentry-backed error boundary wrapper for the app.
 * Annotates captured errors with boundary metadata and switches fallback UI based on deployment.
 *
 * @param {ErrorBoundaryProps} props - React subtree to guard.
 * @returns {JSX.Element} Children wrapped in a Sentry.ErrorBoundary instance.
 */
const ErrorBoundary = (props: ErrorBoundaryProps): JSX.Element => {
  const { children } = props;
  const isProductionChunkLoadFailure = (error: unknown): boolean => {
    return env.DEPLOYMENT !== "local" && isChunkLoadFailure(error);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope, error, componentStack) => {
        scope.setTag("boundary", "ErrorBoundary");
        scope.setExtra("componentStack", componentStack);

        if (!isProductionChunkLoadFailure(error)) {
          return;
        }

        scope.setTag("error.kind", "chunk_load_failure");
        scope.setLevel("warning");

        if (!claimChunkLoadReload(window.location.pathname)) {
          scope.setTag("chunk_load.reload", "already_attempted");
          return;
        }

        pendingChunkLoadReload = true;
        scope.setTag("chunk_load.reload", "scheduled");
        scope.addEventProcessor(() => null);
      }}
      onError={(error) => {
        if (pendingChunkLoadReload && isProductionChunkLoadFailure(error)) {
          pendingChunkLoadReload = false;
          window.location.reload();
        }
      }}
      fallback={({ error, componentStack }) => {
        if (env.DEPLOYMENT === "local") {
          return (
            <LocalErrorFallback error={error} componentStack={componentStack} />
          );
        }

        if (
          isChunkLoadFailure(error) &&
          (pendingChunkLoadReload ||
            !hasChunkLoadReloadAttempt(window.location.pathname))
        ) {
          return <FullPageLoader />;
        }

        return <GlobalErrorPage />;
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
